import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { Fraunces_500Medium, useFonts } from '@expo-google-fonts/fraunces';
import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Clipboard from 'expo-clipboard';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { useFormFooterInset } from '@/components/mobile-bottom-nav';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingButton } from '@/components/ui/loading-button';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  ControlledOptionRows,
  ControlledSwitchRow,
  FieldBlock,
} from '@/components/services/wizard/field';
import { useProviderAvailability } from '@/hooks/use-appointments';
import { usePortfolioPhotos } from '@/hooks/use-provider-portfolio';
import { useMyPublicProfile } from '@/hooks/use-public-profile';
import { useProviderServices } from '@/hooks/use-services';
import { useAuthStore } from '@/store/authStore';
import { showToast } from '@/store/toastStore';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { cn } from '@/utils/cn';
import { dayParts, formatSlotTime } from '@/utils/format-appointment';

import { PostCard } from './post-generator/post-card';
import { SAVE_LABEL, savePosts, sharePost, type ExportItem } from './post-generator/post-export';
import {
  buildCaption,
  buildPalettes,
  defaultText,
  FORMAT_LABEL,
  FORMAT_RATIO,
  formatPostDuration,
  formatPostPrice,
  PHOTO_TYPES,
  photoForService,
  POST_FORMATS,
  postSchema,
  STEP_FIELDS,
  type PostData,
  type PostForm,
  type PostFormat,
  type PostService,
  type PostSlotDay,
  type PostStyle,
  type PostType,
} from './post-generator/post-model';
import { useThemedHex } from '@/hooks/use-theme-colors';

const PUBLIC_BASE_URL = 'https://controlapag.com.br';
const DEFAULT_BRAND_COLOR = '#FD5001';
const PRIMARY = '#FD5001';
/** Largura da imagem na tela de exportação: × 3 = 1080px. */
const EXPORT_BASE_WIDTH = 360;
const EXPORT_WIDTH = 1080;

const TYPE_INFO: Record<
  PostType,
  { icon: React.ComponentProps<typeof Ionicons>['name']; label: string; meaning: string }
> = {
  work: {
    icon: 'cut-outline',
    label: 'Trabalho concluído',
    meaning: 'Foto do portfólio com o nome do serviço',
  },
  beforeAfter: {
    icon: 'git-compare-outline',
    label: 'Antes e depois',
    meaning: 'As duas fotos lado a lado',
  },
  service: {
    icon: 'pricetag-outline',
    label: 'Serviço em destaque',
    meaning: 'Preço, duração e chamada pra agendar',
  },
  openSlots: {
    icon: 'time-outline',
    label: 'Horários livres',
    meaning: 'Puxa da sua agenda da semana',
  },
  card: {
    icon: 'storefront-outline',
    label: 'Cartão do negócio',
    meaning: 'Serviços, preços e QR code',
  },
  promo: { icon: 'flash-outline', label: 'Promoção', meaning: 'Desconto com prazo' },
};

const STYLE_INFO: Record<PostStyle, { label: string; meaning: string }> = {
  editorial: { label: 'Editorial', meaning: 'Foto grande, título em serifa. Cara de revista.' },
  frame: { label: 'Moldura', meaning: 'Fundo claro, foto emoldurada. Limpo e elegante.' },
  block: { label: 'Bloco', meaning: 'Cor da marca chapada, letra gigante.' },
};

const STEPS = ['Objetivo', 'Conteúdo', 'Visual', 'Publicar'];

export function PostGeneratorScreen() {
  const { data: profile, isLoading } = useMyPublicProfile();
  const { data: photos } = usePortfolioPhotos();
  const { data: servicesPage } = useProviderServices({ isActive: true, limit: 30 });
  const businessName = useAuthStore((state) => state.user?.providerProfile?.title) ?? '';
  const [fontsLoaded] = useFonts({ Fraunces_500Medium });

  const [weekOffset, setWeekOffset] = useState(0);
  const services = useMemo(() => servicesPage?.data ?? [], [servicesPage]);
  const schedulable = services.find((s) => !!s.durationMinutes);
  const range = useMemo(() => {
    const start = new Date(Date.now() + weekOffset * 7 * 86_400_000);
    return {
      from: start.toISOString(),
      to: new Date(start.getTime() + 7 * 86_400_000).toISOString(),
    };
  }, [weekOffset]);
  const { data: availability } = useProviderAvailability({
    serviceId: schedulable?.id,
    from: range.from,
    to: range.to,
  });

  const org = profile?.organization ?? null;
  const orgPath = org?.slug ? `@${org.slug}/professional/${org.providerId}` : null;
  const ownPath = profile?.slug && profile.isPublic ? `@${profile.slug}` : null;
  const path = ownPath ?? orgPath;

  const data = useMemo<PostData | null>(() => {
    if (!profile || !path) return null;
    const postServices: PostService[] = services.map((s) => {
      const hidden = s.priceDisplayMode === PRICE_DISPLAY_MODE.HIDDEN || !s.defaultPrice;
      return {
        id: s.id,
        name: s.name,
        price: hidden ? null : formatPostPrice(Number(s.defaultPrice)),
        durationMinutes: s.durationMinutes ?? null,
      };
    });
    const now = Date.now();
    const slots: PostSlotDay[] = (availability?.days ?? [])
      .map((day) => {
        const free = day.slots.filter(
          (s) => s.remaining > 0 && new Date(s.startAt).getTime() > now,
        );
        // Até 3 horários espalhados pelo dia: manhã, meio e fim.
        const picks =
          free.length <= 3
            ? free
            : [free[0], free[Math.floor(free.length / 2)], free[free.length - 1]];
        const weekday = dayParts(day.date).weekday;
        return {
          weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
          times: picks.map((s) => formatSlotTime(s.startAt)),
        };
      })
      .filter((d) => d.times.length > 0);
    return {
      businessName,
      logoUrl: profile.logoUrl,
      category: profile.category,
      place: profile.neighborhood
        ? [profile.neighborhood, profile.city].filter(Boolean).join(', ')
        : profile.city,
      displayUrl: `${PUBLIC_BASE_URL.replace('https://', '')}/${path}`,
      publicUrl: `${PUBLIC_BASE_URL}/${path}`,
      photos: photos ?? [],
      services: postServices,
      slots,
    };
  }, [profile, path, services, availability, photos, businessName]);

  if (isLoading || !profile || !fontsLoaded) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (!path || !data) {
    return (
      <View className="flex-1 items-center justify-center gap-3 p-8">
        <Ionicons className="text-neutral-400" name="megaphone-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">
          Publique seu perfil primeiro
        </ThemedText>
        <ThemedText className="text-center text-foreground/60">
          {org
            ? `Os posts levam para a sua página dentro de ${org.name}. Quando o dono publicar a página, dá para criá-los.`
            : 'Os posts levam para a sua página pública. Ative o perfil público para criá-los.'}
        </ThemedText>
        <Button title="Voltar" onPress={() => router.back()} />
      </View>
    );
  }

  return (
    <PostWizard
      data={data}
      brandColor={profile.brandColor ?? DEFAULT_BRAND_COLOR}
      hasSchedule={!!schedulable}
      weekOffset={weekOffset}
      onWeekOffset={setWeekOffset}
    />
  );
}

interface WizardProps {
  data: PostData;
  brandColor: string;
  hasSchedule: boolean;
  weekOffset: number;
  onWeekOffset: (v: number) => void;
}

function PostWizard({ data, brandColor, hasSchedule, weekOffset, onWeekOffset }: WizardProps) {
  const th = useThemedHex();
  const { width: windowWidth } = useWindowDimensions();
  // Largura real da área da página (sem sidebar), medida no primeiro layout.
  const [areaWidth, setAreaWidth] = useState(windowWidth - 40);
  /** 1 coluna · 2 colunas (conteúdo + prévia) · 3 colunas (trilha + conteúdo + prévia). */
  const columns = areaWidth >= 1080 ? 3 : areaWidth >= 700 ? 2 : 1;
  const wide = columns === 3;
  const sidePreview = columns >= 2;
  const bottomInset = useFormFooterInset();
  const palettes = useMemo(() => buildPalettes(brandColor), [brandColor]);

  const { control, trigger, setValue, getValues, getFieldState } = useForm<PostForm>({
    resolver: zodResolver(postSchema),
    mode: 'onTouched',
    defaultValues: {
      type: undefined,
      photoId: null,
      serviceId: null,
      kicker: '',
      title: '',
      subtitle: '',
      highlight: '',
      showPrice: true,
      showQr: false,
      style: 'editorial',
      format: 'story',
      palette: 0,
    },
  });
  const values = useWatch({ control }) as PostForm;
  const colors = palettes[values.palette] ?? palettes[0];

  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [caption, setCaption] = useState('');
  const [captionVariant, setCaptionVariant] = useState(0);
  const [exporting, setExporting] = useState<'save' | 'share' | null>(null);

  // --- tipos disponíveis com o que o prestador já tem ---
  const beforeAfterPhotos = data.photos.filter((p) => p.beforeUrl);
  const unavailable: Partial<Record<PostType, string>> = {};
  if (!data.photos.length) {
    unavailable.work = 'Adicione fotos ao portfólio primeiro';
    unavailable.service = 'Adicione fotos ao portfólio primeiro';
  }
  if (!beforeAfterPhotos.length) unavailable.beforeAfter = 'Nenhuma foto com antes e depois';
  if (!data.services.length) unavailable.service = 'Cadastre um serviço primeiro';
  if (!hasSchedule) unavailable.openSlots = 'Nenhum serviço com horário na agenda';
  if (!data.services.some((s) => s.price)) unavailable.card = 'Nenhum serviço com preço público';

  const chooseType = (type: PostType) => {
    if (unavailable[type]) {
      showToast(unavailable[type]!, 'info');
      return;
    }
    const service = type === 'service' ? data.services[0] : undefined;
    const photo =
      type === 'beforeAfter'
        ? beforeAfterPhotos[0]
        : type === 'service'
          ? photoForService(data, service?.id ?? null)
          : PHOTO_TYPES.includes(type)
            ? data.photos[0]
            : undefined;
    const text = defaultText(type, data, photo, service);
    const opts = { shouldValidate: false, shouldDirty: false };
    setValue('type', type, { shouldValidate: true });
    setValue('photoId', photo?.id ?? null, opts);
    setValue('serviceId', service?.id ?? null, opts);
    setValue('kicker', text.kicker, opts);
    setValue('title', text.title, opts);
    setValue('subtitle', text.subtitle, opts);
    setValue('highlight', text.highlight, opts);
    setValue('showPrice', !!service?.price, opts);
    setValue('showQr', type === 'card', opts);
  };

  /** Troca de foto/serviço atualiza o texto que o prestador ainda não mexeu. */
  const refreshText = (photoId: string | null, serviceId: string | null) => {
    const photo = data.photos.find((p) => p.id === photoId);
    const service = data.services.find((s) => s.id === serviceId);
    const text = defaultText(values.type, data, photo, service);
    (['kicker', 'title', 'subtitle', 'highlight'] as const).forEach((field) => {
      if (!getFieldState(field).isDirty) setValue(field, text[field]);
    });
  };

  const prevService = useRef<string | null>(null);
  useEffect(() => {
    if (values.type !== 'service' || prevService.current === values.serviceId) {
      prevService.current = values.serviceId;
      return;
    }
    prevService.current = values.serviceId;
    const photo = photoForService(data, values.serviceId);
    if (photo) setValue('photoId', photo.id);
    const service = data.services.find((s) => s.id === values.serviceId);
    setValue('showPrice', !!service?.price);
    refreshText(photo?.id ?? null, values.serviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.serviceId]);

  // --- portão entre etapas ---
  const firstError = (fields: (keyof PostForm)[]) =>
    fields.map((f) => getFieldState(f).error?.message).find(Boolean);

  const goTo = async (target: number) => {
    if (target <= step) {
      setStep(target);
      return;
    }
    for (let s = step; s < target; s++) {
      const ok = await trigger(STEP_FIELDS[s]);
      if (!ok) {
        setStep(s);
        showToast(firstError(STEP_FIELDS[s]) ?? 'Complete os campos destacados.', 'warning');
        return;
      }
    }
    if (target === 3) setCaption(buildCaption(getValues(), data, captionVariant));
    setStep(target);
    setReached((r) => Math.max(r, target));
  };

  // --- exportação ---
  const exportRefs = {
    story: useRef<View>(null),
    portrait: useRef<View>(null),
    square: useRef<View>(null),
  };
  const slug = data.displayUrl.split('@')[1]?.split('/')[0] ?? 'post';
  const exportItem = (format: PostFormat): ExportItem => ({
    ref: exportRefs[format],
    fileName: `post-${slug}-${format}.png`,
    width: EXPORT_WIDTH,
    height: Math.round(EXPORT_WIDTH / FORMAT_RATIO[format]),
  });

  const handleSave = async () => {
    setExporting('save');
    try {
      const ok = await savePosts(POST_FORMATS.map(exportItem));
      if (!ok) showToast('Permissão para salvar nas fotos é necessária.', 'error');
      else showToast('As 3 imagens foram salvas.', 'success');
    } catch {
      showToast('Erro ao gerar as imagens.', 'error');
    } finally {
      setExporting(null);
    }
  };

  const handleShare = async () => {
    setExporting('share');
    try {
      await sharePost(exportItem(values.format));
    } catch {
      showToast('Erro ao gerar a imagem.', 'error');
    } finally {
      setExporting(null);
    }
  };

  const copy = async (text: string, message: string) => {
    await Clipboard.setStringAsync(text);
    showToast(message, 'success');
  };

  // --- medidas ---
  const RAIL = 220;
  const GAP = 32;
  const previewCol = columns === 3 ? 300 : 260;
  const mainWidth = Math.floor(
    columns === 3
      ? Math.min(620, areaWidth - RAIL - previewCol - 2 * GAP)
      : columns === 2
        ? Math.min(620, areaWidth - previewCol - GAP)
        : areaWidth,
  );
  const previewWidth = sidePreview
    ? values.format === 'story'
      ? previewCol - 40
      : previewCol - 20
    : Math.min(values.format === 'story' ? 200 : 240, areaWidth);
  const twoUpTypes = mainWidth >= 520;
  const styleThumbWidth = Math.floor((mainWidth - 2 * 12 - 3 * 20 - 3 * 4) / 3);
  const kitWidth = Math.floor((mainWidth - 2 * 12) / 3);

  const railValue = (i: number): string | null => {
    if (i > reached) return null;
    if (i === 0) return values.type ? TYPE_INFO[values.type].label : null;
    if (i === 1) return values.title || null;
    if (i === 2) return `${STYLE_INFO[values.style].label} · ${FORMAT_LABEL[values.format]}`;
    return 'Baixar, legenda, link';
  };

  const preview = values.type ? (
    <PostCard spec={values} data={data} colors={colors} width={previewWidth} />
  ) : (
    <View
      style={{ width: previewWidth, height: previewWidth / FORMAT_RATIO.story }}
      className="items-center justify-center gap-2 rounded-2xl border border-dashed border-neutral-300 p-6"
    >
      <Ionicons className="text-neutral-400" name="image-outline" size={28} />
      <ThemedText className="text-center text-xs text-neutral-400">
        Escolha o que divulgar para ver o post
      </ThemedText>
    </View>
  );

  const footer = (
    <View
      className="flex-row gap-3 border-t border-neutral-900/5 bg-canvas px-5 pt-3"
      style={{ paddingBottom: 8 + bottomInset }}
    >
      {step > 0 ? (
        <PressableScale
          haptic
          onPress={() => void goTo(step - 1)}
          className="h-12 w-24 items-center justify-center rounded-full border border-neutral-200 bg-card"
        >
          <ThemedText className="font-bold text-sm text-neutral-800">Voltar</ThemedText>
        </PressableScale>
      ) : null}
      <View className="flex-1">
        {step < 3 ? (
          <LoadingButton
            title="Continuar"
            size="lg"
            loading={false}
            onPress={() => void goTo(step + 1)}
          />
        ) : (
          <LoadingButton
            title={SAVE_LABEL}
            size="lg"
            loading={exporting === 'save'}
            disabled={!!exporting}
            onPress={() => void handleSave()}
          />
        )}
      </View>
    </View>
  );

  return (
    <View className="flex-1">
      {/* Palco de exportação: fica atrás da tela, só existe na última etapa. */}
      {step === 3 ? (
        <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0 }}>
          {POST_FORMATS.map((format) => (
            <View
              key={format}
              ref={exportRefs[format]}
              collapsable={false}
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              <PostCard
                spec={{ ...values, format }}
                data={data}
                colors={colors}
                width={EXPORT_BASE_WIDTH}
                flat
              />
            </View>
          ))}
        </View>
      ) : null}

      <PageLayout
        title="Criar post"
        subtitle="Imagens prontas para divulgar, com a sua marca"
        onBack={() => router.back()}
        footer={footer}
      >
        {!wide ? (
          <ScrollView
            keyboardShouldPersistTaps="handled"
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8 }}
          >
            {STEPS.map((label, i) => (
              <Pressable
                key={label}
                onPress={() => void goTo(i)}
                className={cn(
                  'h-9 flex-row items-center gap-2 rounded-full border px-3',
                  i === step ? 'border-transparent bg-primary/10' : 'border-neutral-200 bg-card',
                )}
              >
                <StepDot index={i} step={step} reached={reached} />
                <ThemedText
                  className={cn(
                    'text-xs',
                    i === step ? 'font-semibold text-neutral-900' : 'text-neutral-500',
                  )}
                >
                  {label}
                </ThemedText>
              </Pressable>
            ))}
          </ScrollView>
        ) : null}

        <View
          onLayout={(e) => {
            const w = Math.floor(e.nativeEvent.layout.width);
            if (Math.abs(w - areaWidth) > 1) setAreaWidth(w);
          }}
          className={cn(sidePreview ? 'flex-row items-start' : 'gap-4')}
          style={sidePreview ? { gap: GAP } : undefined}
        >
          {wide ? (
            <View style={{ width: RAIL }} className="gap-1">
              {STEPS.map((label, i) => {
                const v = railValue(i);
                return (
                  <Pressable
                    key={label}
                    onPress={() => void goTo(i)}
                    className={cn(
                      'flex-row gap-3 rounded-xl p-2.5',
                      i === step ? 'bg-primary/10' : '',
                    )}
                  >
                    <StepDot index={i} step={step} reached={reached} />
                    <View className="flex-1">
                      <ThemedText className="font-semibold text-[13px] text-neutral-900">
                        {label}
                      </ThemedText>
                      <ThemedText numberOfLines={1} className="text-xs text-neutral-500">
                        {v ?? '—'}
                      </ThemedText>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          ) : null}

          <View
            className={cn('gap-4', sidePreview ? 'flex-1 items-center' : '')}
            style={{ minWidth: 0 }}
          >
            {!sidePreview && (step === 1 || step === 2) && values.type ? (
              <View className="items-center">{preview}</View>
            ) : null}

            <View
              style={{ width: sidePreview ? mainWidth : undefined, maxWidth: '100%' }}
              className="gap-4"
            >
              {/* 1 · Objetivo */}
              <View style={{ display: step === 0 ? 'flex' : 'none' }} className="gap-4">
                <StepTitle
                  title="O que você quer divulgar hoje?"
                  lead="Cada objetivo monta o post com os seus dados reais."
                />
                <View className="flex-row flex-wrap gap-2.5">
                  {(Object.keys(TYPE_INFO) as PostType[]).map((type) => {
                    const info = TYPE_INFO[type];
                    const selected = values.type === type;
                    const blocked = unavailable[type];
                    return (
                      <PressableScale
                        key={type}
                        haptic
                        onPress={() => chooseType(type)}
                        accessibilityRole="radio"
                        accessibilityState={{ selected }}
                        style={{ width: twoUpTypes ? (mainWidth - 10) / 2 : '100%' }}
                        className={cn(
                          'flex-row items-start gap-3 rounded-2xl border bg-card p-3.5',
                          selected ? 'border-primary bg-primary/5' : 'border-neutral-900/5',
                          blocked ? 'opacity-50' : '',
                        )}
                      >
                        <View
                          className={cn(
                            'h-10 w-10 items-center justify-center rounded-xl',
                            selected ? 'bg-card' : 'bg-neutral-100',
                          )}
                        >
                          <Ionicons
                            name={info.icon}
                            size={19}
                            color={selected ? PRIMARY : th('#525252')}
                          />
                        </View>
                        <View className="flex-1 gap-0.5">
                          <ThemedText className="font-semibold text-sm text-neutral-900">
                            {info.label}
                          </ThemedText>
                          <ThemedText className="text-xs leading-4 text-neutral-500">
                            {blocked ?? info.meaning}
                          </ThemedText>
                        </View>
                      </PressableScale>
                    );
                  })}
                </View>
              </View>

              {/* 2 · Conteúdo */}
              <View style={{ display: step === 1 ? 'flex' : 'none' }} className="gap-4">
                <StepTitle
                  title={values.type ? TYPE_INFO[values.type].label : 'Conteúdo'}
                  lead={
                    values.type === 'promo'
                      ? 'Escreva a oferta do jeito que você fala.'
                      : 'Já veio preenchido. Mude só o que quiser.'
                  }
                />
                <View
                  className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
                  style={{ borderCurve: 'continuous' }}
                >
                  {values.type === 'service' ? (
                    <ControlledOptionRows
                      control={control}
                      name="serviceId"
                      title="Serviço"
                      options={data.services.map((s) => ({
                        value: s.id,
                        label: s.name,
                        meaning:
                          [formatPostDuration(s.durationMinutes), s.price]
                            .filter(Boolean)
                            .join(' · ') || undefined,
                      }))}
                    />
                  ) : null}

                  {values.type && PHOTO_TYPES.includes(values.type) ? (
                    <Controller
                      control={control}
                      name="photoId"
                      render={({ field, fieldState }) => (
                        <FieldBlock
                          title="Foto"
                          note="do seu portfólio"
                          error={fieldState.error?.message}
                        >
                          <ScrollView
                            keyboardShouldPersistTaps="handled"
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            contentContainerStyle={{ gap: 8 }}
                          >
                            {(values.type === 'beforeAfter' ? beforeAfterPhotos : data.photos).map(
                              (p) => (
                                <Pressable
                                  key={p.id}
                                  onPress={() => {
                                    field.onChange(p.id);
                                    refreshText(p.id, values.serviceId);
                                  }}
                                  className={cn(
                                    'h-14 w-14 overflow-hidden rounded-xl border-2',
                                    field.value === p.id ? 'border-primary' : 'border-transparent',
                                  )}
                                >
                                  <PhotoThumb uri={p.url} />
                                </Pressable>
                              ),
                            )}
                          </ScrollView>
                        </FieldBlock>
                      )}
                    />
                  ) : null}

                  <TextField
                    control={control}
                    name="kicker"
                    title="Chamada"
                    placeholder={values.type === 'promo' ? 'Só até sábado' : 'Trabalho de hoje'}
                  />
                  <TextField
                    control={control}
                    name="title"
                    title="Título"
                    placeholder={values.type === 'promo' ? '20% off na terça' : 'Título do post'}
                  />
                  <TextField
                    control={control}
                    name="subtitle"
                    title="Linha de apoio"
                    placeholder={
                      values.type === 'promo'
                        ? 'Corte + barba de R$ 55 por R$ 44'
                        : 'Agende pelo link'
                    }
                  />
                  {values.type === 'promo' ? (
                    <TextField
                      control={control}
                      name="highlight"
                      title="Destaque"
                      placeholder="-20%"
                      short
                    />
                  ) : null}
                  {values.type === 'service' ? (
                    <>
                      <ControlledSwitchRow
                        control={control}
                        name="showPrice"
                        title="Mostrar preço"
                      />
                      {values.showPrice ? (
                        <TextField
                          control={control}
                          name="highlight"
                          title="Preço no post"
                          placeholder="R$ 55"
                          short
                        />
                      ) : null}
                    </>
                  ) : null}
                  {values.type === 'openSlots' ? (
                    <FieldBlock
                      title="Semana"
                      note={`${data.slots.reduce((acc, d) => acc + d.times.length, 0)} horários no post`}
                    >
                      <Segmented
                        options={[
                          { value: 0, label: 'Esta' },
                          { value: 1, label: 'Próxima' },
                        ]}
                        value={weekOffset}
                        onChange={(v) => {
                          onWeekOffset(v);
                          if (!getFieldState('title').isDirty) {
                            setValue(
                              'title',
                              v === 0
                                ? 'Horários livres essa semana'
                                : 'Horários livres semana que vem',
                            );
                          }
                        }}
                      />
                    </FieldBlock>
                  ) : null}
                  <ControlledSwitchRow
                    control={control}
                    name="showQr"
                    title="QR code"
                    note="Pra quem vê o post na tela de outra pessoa"
                  />
                </View>
              </View>

              {/* 3 · Visual */}
              <View style={{ display: step === 2 ? 'flex' : 'none' }} className="gap-4">
                <StepTitle
                  title="Como vai ficar?"
                  lead="Três estilos, todos com a sua cor e o seu link."
                />
                {values.type ? (
                  <View className="flex-row gap-3">
                    {(Object.keys(STYLE_INFO) as PostStyle[]).map((style) => {
                      const selected = values.style === style;
                      return (
                        <PressableScale
                          key={style}
                          haptic
                          onPress={() => setValue('style', style)}
                          className={cn(
                            'flex-1 gap-2 rounded-2xl border bg-card p-2.5',
                            selected ? 'border-primary' : 'border-neutral-900/5',
                          )}
                        >
                          <PostCard
                            spec={{ ...values, style, format: 'portrait' }}
                            data={data}
                            colors={colors}
                            width={styleThumbWidth}
                            flat
                          />
                          <View>
                            <ThemedText className="font-semibold text-[13px] text-neutral-900">
                              {STYLE_INFO[style].label}
                            </ThemedText>
                            {mainWidth >= 560 ? (
                              <ThemedText className="text-[11px] leading-4 text-neutral-500">
                                {STYLE_INFO[style].meaning}
                              </ThemedText>
                            ) : null}
                          </View>
                        </PressableScale>
                      );
                    })}
                  </View>
                ) : null}
                <View
                  className="gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
                  style={{ borderCurve: 'continuous' }}
                >
                  <View className="flex-row flex-wrap items-center justify-between gap-3">
                    <ThemedText className="font-semibold text-[15px] text-neutral-900">
                      Onde vai postar
                    </ThemedText>
                    <Segmented
                      options={[
                        { value: 'story', label: 'Story' },
                        { value: 'portrait', label: 'Feed' },
                        { value: 'square', label: 'Quadrado' },
                      ]}
                      value={values.format}
                      onChange={(v) => setValue('format', v)}
                    />
                  </View>
                  <View className="flex-row flex-wrap items-center justify-between gap-3">
                    <View>
                      <ThemedText className="font-semibold text-[15px] text-neutral-900">
                        Cores
                      </ThemedText>
                      <ThemedText className="text-xs text-neutral-500">
                        a primeira é a da sua marca
                      </ThemedText>
                    </View>
                    <View className="flex-row gap-2">
                      {palettes.map((pair, i) => (
                        <Pressable
                          key={`${pair[0]}-${i}`}
                          onPress={() => setValue('palette', i)}
                          accessibilityLabel={`Paleta ${i + 1}`}
                          className={cn(
                            'h-8 w-8 items-center justify-center rounded-full border-2',
                            values.palette === i ? 'border-neutral-900' : 'border-transparent',
                          )}
                        >
                          <LinearGradient
                            colors={[pair[0], pair[0], pair[1], pair[1]]}
                            locations={[0, 0.55, 0.55, 1]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={{ width: 24, height: 24, borderRadius: 12 }}
                          />
                        </Pressable>
                      ))}
                    </View>
                  </View>
                </View>
              </View>

              {/* 4 · Publicar */}
              <View style={{ display: step === 3 ? 'flex' : 'none' }} className="gap-4">
                <StepTitle
                  title="Pronto. Leve pra onde quiser."
                  lead="Sai nos três tamanhos de uma vez, com a legenda escrita."
                />
                {values.type ? (
                  <View className="flex-row items-end gap-3">
                    {POST_FORMATS.map((format) => (
                      <Pressable
                        key={format}
                        onPress={() => setValue('format', format)}
                        className="items-center gap-1.5"
                      >
                        <View
                          className={cn(
                            'rounded-2xl border-2 p-0.5',
                            values.format === format ? 'border-primary' : 'border-transparent',
                          )}
                        >
                          <PostCard
                            spec={{ ...values, format }}
                            data={data}
                            colors={colors}
                            width={kitWidth - 8}
                            flat
                          />
                        </View>
                        <ThemedText className="text-[11px] text-neutral-500">
                          {FORMAT_LABEL[format]}
                        </ThemedText>
                      </Pressable>
                    ))}
                  </View>
                ) : null}
                <View className="flex-row flex-wrap gap-2">
                  <LoadingButton
                    title={`Compartilhar ${values.format === 'story' ? 'story' : 'feed'}`}
                    variant="outline"
                    size="md"
                    loading={exporting === 'share'}
                    disabled={!!exporting}
                    onPress={() => void handleShare()}
                  />
                  <Button
                    title="Copiar link"
                    variant="outline"
                    size="md"
                    onPress={() => void copy(data.publicUrl, 'Link copiado.')}
                  />
                </View>

                <FieldBlock title="Legenda pronta" note="edite à vontade">
                  <TextInput
                    multiline
                    value={caption}
                    onChangeText={setCaption}
                    className="min-h-[140px] rounded-xl border border-neutral-200 bg-card px-4 py-3 text-sm leading-5 text-neutral-900"
                    style={{ textAlignVertical: 'top', outline: 'none' } as never}
                  />
                </FieldBlock>
                <View className="flex-row gap-2">
                  <Button
                    title="Copiar legenda"
                    variant="outline"
                    size="md"
                    onPress={() => void copy(caption, 'Legenda copiada.')}
                  />
                  <Button
                    title="Outra versão"
                    variant="ghost"
                    size="md"
                    onPress={() => {
                      const next = captionVariant + 1;
                      setCaptionVariant(next);
                      setCaption(buildCaption(getValues(), data, next));
                    }}
                  />
                </View>
              </View>
            </View>
          </View>

          {sidePreview ? (
            <View style={{ width: previewCol }} className="items-center gap-3">
              <ThemedText className="font-semibold text-xs text-neutral-400">Prévia</ThemedText>
              {preview}
            </View>
          ) : null}
        </View>
      </PageLayout>
    </View>
  );
}

// --- peças da tela ----------------------------------------------------------

function StepDot({ index, step, reached }: { index: number; step: number; reached: number }) {
  const done = index !== step && (index < step || index < reached);
  return (
    <View
      className={cn(
        'h-6 w-6 items-center justify-center rounded-full',
        index === step ? 'bg-primary' : done ? 'bg-success-50' : 'bg-neutral-100',
      )}
    >
      {done && index !== step ? (
        <Ionicons name="checkmark" size={13} color="#137A63" />
      ) : (
        <ThemedText
          className={cn(
            'font-bold text-[11px]',
            index === step ? 'text-white' : 'text-neutral-500',
          )}
        >
          {index + 1}
        </ThemedText>
      )}
    </View>
  );
}

function StepTitle({ title, lead }: { title: string; lead: string }) {
  return (
    <View className="gap-1">
      <ThemedText className="font-bold text-xl text-neutral-900">{title}</ThemedText>
      <ThemedText className="text-sm text-neutral-500">{lead}</ThemedText>
    </View>
  );
}

function PhotoThumb({ uri }: { uri: string }) {
  return (
    <View className="flex-1 bg-neutral-200">
      <Image
        source={{ uri }}
        accessibilityLabel="Foto selecionada"
        style={{ width: '100%', height: '100%' }}
        resizeMode="cover"
      />
    </View>
  );
}

function TextField({
  control,
  name,
  title,
  placeholder,
  short,
}: {
  control: ReturnType<typeof useForm<PostForm>>['control'];
  name: 'kicker' | 'title' | 'subtitle' | 'highlight';
  title: string;
  placeholder: string;
  short?: boolean;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FieldBlock title={title} error={fieldState.error?.message}>
          <Input
            value={field.value}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            className={cn('h-11 text-sm', short ? 'w-32' : '')}
          />
        </FieldBlock>
      )}
    />
  );
}

function Segmented<T extends string | number>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <View className="flex-row gap-0.5 self-start rounded-[10px] bg-neutral-100 p-[3px]">
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <Pressable
            key={String(o.value)}
            onPress={() => onChange(o.value)}
            className={cn(
              'h-8 justify-center rounded-lg px-3',
              selected ? 'bg-card shadow-sm' : '',
            )}
          >
            <ThemedText
              className={cn(
                'text-xs',
                selected ? 'font-semibold text-neutral-900' : 'text-neutral-500',
              )}
            >
              {o.label}
            </ThemedText>
          </Pressable>
        );
      })}
    </View>
  );
}
